import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosBackend from '../axios/axios';
import TranslationText from './TranslationText';
import VerticalDivider from './VerticalDivider';

export default function LiveEventButton({ language = 'en', setMenuIsShown }) {
    const [liveEvents, setLiveEvents] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const closeTimerRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        fetchLiveEvents();
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }
        };
    }, []);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                if (closeTimerRef.current) {
                    clearTimeout(closeTimerRef.current);
                    closeTimerRef.current = null;
                }
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    async function fetchLiveEvents() {
        try {
            const res = await axiosBackend.get('/api/Show/CheckLiveEvent');
            if (res.status === 200 && res.data?.responseCode === '200') {
                const result = res.data?.result;
                if (Array.isArray(result)) {
                    setLiveEvents(result.filter(item => Boolean(item?.id || item?.liveLink)));
                } else if (result?.id) {
                    setLiveEvents([result]);
                } else {
                    setLiveEvents([]);
                }
            }
        } catch (error) {
            console.error('Error fetching live events:', error);
            setLiveEvents([]);
        }
    }

    if (!liveEvents.length) {
        return null;
    }

    const handleClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setIsOpen(false);
        if (setMenuIsShown) {
            setMenuIsShown(false);
        }
    };

    const handleMouseEnter = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
        }
        closeTimerRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 600);
    };

    const handleButtonClick = (e) => {
        e.stopPropagation();
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setIsOpen(true);
    };

    // Single event: direct navigation link
    if (liveEvents.length === 1) {
        return (
            <>
                <Link
                    to={`/showDetails?id=${liveEvents[0]?.id}`}
                    className="menuItem roboto-regular d-flex align-items-center"
                    onClick={handleClose}
                >
                    <LiveLabel />
                </Link>
                <VerticalDivider />
            </>
        );
    }

    // Multiple events: dropdown menu
    return (
        <>
            <div
                ref={containerRef}
                className="liveEventDropdownContainer"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleButtonClick}
                    className="menuItem roboto-regular liveDropdownBtn d-flex align-items-center"
                    style={{ cursor: 'pointer' }}
                    aria-expanded={isOpen}
                >
                    <LiveLabel />
                    <i className={`fa fa-chevron-down liveChevron ${language === 'ar' ? 'rtlChevron' : ''} ${isOpen ? 'open' : ''}`} />
                </div>

                {isOpen && (
                    <LiveDropdownMenu
                        events={liveEvents}
                        language={language}
                        onClose={handleClose}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />
                )}
            </div>
            <VerticalDivider />
        </>
    );
}

/* -------------------------------------------------------------------------- */
/*                               Sub-Components                               */
/* -------------------------------------------------------------------------- */

function LiveLabel() {
    return (
        <>
            <div className="bg-danger rounded-circle liveCircle my-auto mx-2" />
            <TranslationText defaultText="Live" textkey="web.liveEvent" key="Live" />
        </>
    );
}

function LiveDropdownMenu({ events, language, onClose, onMouseEnter, onMouseLeave }) {
    const isArabic = language === 'ar';

    return (
        <div
            className="liveDropdownMenu"
            style={isArabic ? { right: 0, left: 'auto', textAlign: 'right' } : { left: 0, right: 'auto', textAlign: 'left' }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="liveDropdownHeader">
                <span className="liveDropdownHeaderTitle">
                    {isArabic ? 'الفعاليات المباشرة' : 'LIVE EVENTS'}
                </span>
                <span className="liveDropdownHeaderBadge">
                    {events.length}
                </span>
            </div>
            <div className="liveDropdownList">
                {events.map((event, index) => (
                    <LiveDropdownItem
                        key={event?.id || index}
                        event={event}
                        isArabic={isArabic}
                        onSelect={onClose}
                    />
                ))}
            </div>
        </div>
    );
}

function LiveDropdownItem({ event, isArabic, onSelect }) {
    return (
        <Link
            to={`/showDetails?id=${event?.id}`}
            className={`liveDropdownItem ${isArabic ? 'rtlItem' : ''}`}
            onClick={onSelect}
        >
            <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center flex-grow-1 overflow-hidden me-2">
                    <span className={`liveIndicatorDot flex-shrink-0 ${isArabic ? 'rtlDot' : ''}`} />
                    <div className="liveDropdownItemTitle text-truncate">
                        {event?.eventName}
                    </div>
                </div>
                <i className={`fa ${isArabic ? 'fa-angle-left' : 'fa-angle-right'} liveItemArrow`} />
            </div>
        </Link>
    );
}
